import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { formatCount, formatDate, formatDateTime, formatUsd, formatWindow } from './format';

const PanelWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 16px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
`;

const StatsWrapper = styled.div`
  opacity: ${({ $dimmed }) => ($dimmed ? 0.55 : 1)};
`;

const UpdatingPill = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 999px;
  padding: 2px 10px;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  padding: 4px 0;
`;

const WarningBox = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.error};
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
`;

const InfoBox = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
`;

const ErrorText = styled(Body5)`
  color: ${({ theme }) => theme.themeColors.error};
`;

export const BackfillPreviewPanel = ({ preview, isLoading, isReestimating, error, scope, tickerCount }) => {
  if (error) {
    return (
      <PanelWrapper role="alert">
        {/* Verbatim server message. A rejection must not read as a status line in the same type and
            colour as the stats it replaced — the run-blocker hint below already says "see the error
            above", which only works if it looks like an error. */}
        <ErrorText>{error}</ErrorText>
      </PanelWrapper>
    );
  }

  if (preview == null && isLoading) {
    return (
      <PanelWrapper>
        <Body5 isLowContrast>Estimating…</Body5>
      </PanelWrapper>
    );
  }

  if (preview == null) {
    return (
      <PanelWrapper>
        <Body5 isLowContrast>Choose a spec, universe, and date window to see a cost estimate.</Body5>
      </PanelWrapper>
    );
  }

  const {
    candidate_count,
    cost_estimate_usd,
    resolved_from,
    resolved_to,
    universe_size,
    over_ceiling,
    cost_ceiling_usd,
    knowable_to,
    force_knowable_at_to_scoring_time,
  } = preview;

  // Read off the server's echo rather than a prop, so the panel describes the estimate it is showing
  // — and an estimate from a fapi predating the field renders exactly as before.
  const stamped = force_knowable_at_to_scoring_time === true;

  const cap = typeof cost_ceiling_usd === 'number' && cost_ceiling_usd > 0 ? cost_ceiling_usd : 1000;
  const overPercent = typeof cost_estimate_usd === 'number' ? Math.round((cost_estimate_usd / cap - 1) * 100) : 0;

  const showUnresolved =
    typeof tickerCount === 'number' &&
    tickerCount > 0 &&
    typeof universe_size === 'number' &&
    universe_size < tickerCount;

  return (
    <PanelWrapper>
      {isReestimating && (
        <Container bottom={8}>
          <UpdatingPill>
            <Body7 isLowContrast>Updating estimate…</Body7>
          </UpdatingPill>
        </Container>
      )}

      <StatsWrapper $dimmed={isReestimating}>
        <StatRow>
          <Body5 bold>Candidates</Body5>
          <Body5>{formatCount(candidate_count)}</Body5>
        </StatRow>
        <StatRow>
          <Body5 bold>Estimated cost</Body5>
          <Body5>{formatUsd(cost_estimate_usd)}</Body5>
        </StatRow>
        <StatRow>
          <Body5 bold>Resolved window</Body5>
          {/* A stamped estimate's window ends at the instant it was taken (knowable_to); the run's will
              end at its own, a moment later, when it is started. */}
          <Body5>
            {stamped
              ? `${formatDate(resolved_from)} → ${formatDateTime(knowable_to)}`
              : formatWindow(resolved_from, resolved_to)}
          </Body5>
        </StatRow>
        {stamped && (
          <Container top={4}>
            <Body7 isLowContrast>
              Ends when the run is started; this estimate counted occasions knowable as of that moment.
            </Body7>
          </Container>
        )}
        <StatRow>
          <Body5 bold>Universe size</Body5>
          <Body5>{universe_size == null ? 'Whole corpus' : formatCount(universe_size)}</Body5>
        </StatRow>

        {showUnresolved && (
          <Container top={4}>
            <Body7 isLowContrast>
              {formatCount(universe_size)} of {formatCount(tickerCount)} tickers matched —{' '}
              {formatCount(tickerCount - universe_size)} didn’t resolve.
            </Body7>
          </Container>
        )}

        <Container top={8}>
          <Body7 isLowContrast>
            Cost is a conservative upper-bound estimate, not billed spend — actual usage is typically much lower.
          </Body7>
        </Container>

        {candidate_count === 0 && (
          <InfoBox>
            <Body5>
              No documents match this spec, universe, and window — nothing to score. Widen the window or universe.
            </Body5>
          </InfoBox>
        )}

        {over_ceiling && (
          <WarningBox>
            <Body5 bold>
              Estimated {formatUsd(cost_estimate_usd)}, about {overPercent}% over the {formatUsd(cap)} cap.
            </Body5>
            <Container top={4}>
              <Body5>Narrow the date window or universe until the estimate drops below the cap to start the run.</Body5>
            </Container>
            {scope === 'full_history' && (
              <Container top={4}>
                <Body7 isLowContrast>Switch Scope to Latest per company for far fewer candidates.</Body7>
              </Container>
            )}
          </WarningBox>
        )}
      </StatsWrapper>
    </PanelWrapper>
  );
};
